import { useMemo } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { LOGIN, REGISTER } from "../graphql/operations/auth";
import { VERIFY_TWO_FACTOR_CHALLENGE } from "../graphql/operations/twoFactor";
import { GET_ME, UPDATE_USER } from "../graphql/operations/user";
import { orgKey } from "../lib/orgKey";
import { isJwtExpired } from "../lib/jwt";
import { rememberShopifyShop } from "../lib/shopifyAdmin";
import { buildOrgPermissionChecker, type OrgPermissionChecker } from "../constants/orgPermissions";
import { useSetupAssistantStore } from "./setupAssistantStore";

export interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    isEmailVerified: boolean;
    isManagedSupport?: boolean;
    managedSupportRole?: string | null;
    avatar?: string;
    preferences: {
        theme: string;
        notifications: {
            email: boolean;
            push: boolean;
        };
        // Null/absent means follow the organization's default.
        interfaceLanguage?: string | null;
    };
    twoFactor?: {
        enabled: boolean;
        enabledAt?: string | null;
        lastVerifiedAt?: string | null;
        recoveryCodesRemaining: number;
    };
    organizations: Array<{
        id: string;
        name: string;
        domain: string;
        logo?: string;
        settings?: {
            allowedDomains: string[];
            maxChatSessions: number;
            retentionDays: number;
            enableAnalytics: boolean;
            interfaceLanguage?: string | null;
            dailyReportsSettings?: {
                enabled?: boolean;
                recipients?: string[];
                mediums?: string[];
                frequency?: string;
                scheduleTime?: string;
                whatsappGroupId?: string;
                teamsWebhookUrl?: string;
            };
            navigation?: {
                hiddenGroupIds?: string[] | null;
                pinnedCollections?: { collectionId: string; label: string }[] | null;
            } | null;
        };
        security?: {
            twoFactorRequired: boolean;
            twoFactorRequiredRoles: string[];
            twoFactorEnforcedAt?: string | null;
            twoFactorEnforcedBy?: string | null;
        };
        // Capability keys this user holds here, resolved server-side from their
        // role and the workspace's policy. Absent on a payload that predates the
        // field, which `buildOrgPermissionChecker` treats as "unknown" rather
        // than "none".
        myPermissions?: string[] | null;
        members: Array<{
            role: string;
            user: {
                id: string;
                email: string;
                firstName?: string;
                lastName?: string;
            };
        }>;
        subscription?: {
            plan: string;
            status: string;
            billingCycle: string;
            currentPeriodStart: string;
            currentPeriodEnd: string;
            usage: Array<{
                code: string;
                count: number;
                limit: number;
                limitExceeded: boolean;
                lastUsageAt: string;
            }>;
        };
        // Consumption counters live on the organization, not the plan snapshot:
        // this is the array `canPerformAction` reads when it decides whether a
        // visitor may send another message.
        usage?: Array<{
            code: string;
            count: number;
            lastUsageAt?: string | null;
        }>;
        billingCurrency?: string | null;
        outboundCallingEnabled?: boolean;
        isShopifyManaged?: boolean;
        shopifyBillingRequired?: boolean;
        shopifyShopDomain?: string | null;
    }>;
    subscription?: {
        plan: string;
        status: string;
        billingCycle: string;
        currentPeriodStart: string;
        currentPeriodEnd: string;
    };
    createdAt: string;
    updatedAt: string;
}

export interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    currentOrganization: string | null;
    orgRole: string | null;
    userRole: string | null;
    _hasHydrated: boolean;
    // Set when `login` returns `requiresTwoFactor: true`. The Login page
    // swaps to the TOTP / recovery-code form and posts `verifyTwoFactor`
    // with this token. Cleared on success or after `logout` / `clearError`.
    twoFactorChallenge: {
        challengeToken: string;
        email: string;
    } | null;
    // Surfaced after a successful login when org policy requires 2FA but
    // the user hasn't enrolled. The Settings page reads this to auto-route
    // to the setup wizard immediately after dashboard hydration.
    twoFactorSetupRequired: boolean;
    // Non-null when this session was minted by the Shopify embedded-admin SSO
    // exchange. Persisted because it is the only shop-identity signal that
    // survives a reload, and the billing gate needs it to stay closed even
    // when the org lookup can't resolve (see `useShopifyBillingGate`).
    shopifySession: { shopDomain: string } | null;
    // Set when the session ended on its own rather than by the user asking —
    // `App` turns it into a redirect to /login and the Login page explains why.
    // Deliberately not persisted: it describes this page's transition, and a
    // reload of /login should not keep re-announcing it.
    sessionExpired: boolean;
}

const getOrgRole = (user: User | null, orgId: string | null): string | null => {
    if (!user || !orgId) return null;
    const org = user.organizations.find((o) => orgKey(o) === orgId);
    const member = org?.members?.find((m) => m.user.id === user.id);
    return member?.role || null;
};

/**
 * The capability keys the server says this user holds in an organization.
 *
 * Derived rather than stored: it lives on the persisted `user`, so a second copy
 * in state would be one more thing to keep in step on every login, org switch
 * and `fetchMe`. `null` means the payload did not carry the field.
 */
export const getOrgPermissions = (user: User | null, orgId: string | null): string[] | null => {
    if (!user || !orgId) return null;
    const org = user.organizations.find((o) => orgKey(o) === orgId);
    return org?.myPermissions ?? null;
};

export type LoginResult =
    | { kind: "success" }
    | { kind: "twoFactor" }
    | { kind: "error"; message?: string };

export interface AuthActions {
    // Authentication actions
    login: (
        email: string,
        password: string,
        captchaToken: string,
        client: ApolloClient<NormalizedCacheObject>,
    ) => Promise<LoginResult>;
    register: (input: RegisterInput, client: ApolloClient<NormalizedCacheObject>) => Promise<boolean>;
    logout: () => void;
    /** Same teardown as `logout`, but flagged as involuntary. Prefer `endUserSession`. */
    expireSession: () => void;
    clearSessionExpired: () => void;
    setAuth: (data: { user: User; token: string; refreshToken: string; expiresIn: number; organizationId?: string | null; shopDomain?: string | null }) => void; // New action
    // 2FA challenge step — completes a login that returned `requiresTwoFactor: true`.
    verifyTwoFactor: (
        code: string,
        useRecoveryCode: boolean,
        client: ApolloClient<NormalizedCacheObject>,
    ) => Promise<boolean>;
    cancelTwoFactor: () => void;
    clearTwoFactorSetupRequired: () => void;

    // User management
    fetchMe: (client: ApolloClient<NormalizedCacheObject>, forceRefresh?: boolean) => Promise<void>;
    updateUser: (input: UpdateUserInput, client: ApolloClient<NormalizedCacheObject>) => Promise<boolean>;

    // Organization management
    setCurrentOrganization: (organizationId: string) => void;
    clearError: () => void;
    initializeAuth: () => void;
}

export interface LoginInput {
    email: string;
    password: string;
    captchaToken: string;
}

export interface RegisterInput {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName?: string;
    domain?: string;
    industry?: string;
    platform?: string;
    captchaToken: string;
    referralCode?: string;
    /** Shared with the browser CompleteRegistration event and Meta CAPI. */
    registrationEventId?: string;
    eventSourceUrl?: string;
    fbp?: string | null;
    fbc?: string | null;
}

export interface UpdateUserInput {
    firstName?: string;
    lastName?: string;
    avatar?: string;
    preferences?: {
        theme?: string;
        notifications?: {
            email?: boolean;
            push?: boolean;
        };
        interfaceLanguage?: string | null;
    };
}

const CLEARED_SESSION = {
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
    currentOrganization: null,
    orgRole: null,
    userRole: null,
    twoFactorChallenge: null,
    twoFactorSetupRequired: false,
    shopifySession: null,
} satisfies Partial<AuthState>;

// Plain in-memory stores survive a logout, since signing out is a state change
// rather than a reload. Anything holding the departing user's own data has to
// be emptied by hand.
const discardSessionData = () => {
    useSetupAssistantStore.getState().reset();
    // Imported lazily to avoid the cycle through the Apollo client, which
    // reaches back into this store for the auth header.
    import("../lib/apollo")
        .then(({ apolloClient }) => apolloClient.clearStore())
        .catch((error) => console.warn("Failed to clear Apollo cache on logout:", error));
};

const useAuthStore = create<AuthState & AuthActions>()(
    persist(
        (set, get) => ({
            // Initial state
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            currentOrganization: null,
            orgRole: null,
            userRole: null,
            _hasHydrated: false,
            twoFactorChallenge: null,
            twoFactorSetupRequired: false,
            shopifySession: null,
            sessionExpired: false,

            // Authentication actions
            login: async (
                email: string,
                password: string,
                captchaToken: string,
                client: ApolloClient<NormalizedCacheObject>,
            ): Promise<LoginResult> => {
                try {
                    set({ isLoading: true, error: null, twoFactorChallenge: null });

                    const { data } = await client.mutate({
                        mutation: LOGIN,
                        variables: { input: { email, password, captchaToken } },
                    });

                    if (data?.login) {
                        const {
                            token,
                            user,
                            requiresTwoFactor,
                            challengeToken,
                            twoFactorSetupRequired,
                        } = data.login;

                        if (requiresTwoFactor && challengeToken) {
                            set({
                                isLoading: false,
                                error: null,
                                twoFactorChallenge: { challengeToken, email },
                            });
                            return { kind: "twoFactor" };
                        }

                        if (token && user) {
                            localStorage.setItem("token", token);

                            set({
                                user,
                                token,
                                isAuthenticated: true,
                                isLoading: false,
                                error: null,
                                currentOrganization: user.organizations?.[0]?.id || null,
                                orgRole: getOrgRole(user, user.organizations?.[0]?.id || null),
                                userRole: user.role,
                                twoFactorChallenge: null,
                                twoFactorSetupRequired: !!twoFactorSetupRequired,
                                shopifySession: null,
                            });
                            return { kind: "success" };
                        }
                    }

                    set({ isLoading: false, error: "Login failed" });
                    return { kind: "error", message: "Login failed" };
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Login failed";
                    set({ isLoading: false, error: errorMessage });
                    return { kind: "error", message: errorMessage };
                }
            },

            verifyTwoFactor: async (
                code: string,
                useRecoveryCode: boolean,
                client: ApolloClient<NormalizedCacheObject>,
            ): Promise<boolean> => {
                const challenge = get().twoFactorChallenge;
                if (!challenge) {
                    set({ error: "Two-factor session expired. Please log in again." });
                    return false;
                }
                try {
                    set({ isLoading: true, error: null });
                    const { data } = await client.mutate({
                        mutation: VERIFY_TWO_FACTOR_CHALLENGE,
                        variables: {
                            input: {
                                challengeToken: challenge.challengeToken,
                                code,
                                useRecoveryCode,
                            },
                        },
                    });

                    const payload = data?.verifyTwoFactorChallenge;
                    if (payload?.token && payload.user) {
                        localStorage.setItem("token", payload.token);
                        set({
                            user: payload.user,
                            token: payload.token,
                            isAuthenticated: true,
                            isLoading: false,
                            error: null,
                            currentOrganization: payload.user.organizations?.[0]?.id || null,
                            orgRole: getOrgRole(payload.user, payload.user.organizations?.[0]?.id || null),
                            userRole: payload.user.role,
                            twoFactorChallenge: null,
                            twoFactorSetupRequired: false,
                            shopifySession: null,
                        });
                        return true;
                    }
                    set({ isLoading: false, error: "Verification failed" });
                    return false;
                } catch (error: any) {
                    const message = error.graphQLErrors?.[0]?.message || error.message || "Verification failed";
                    set({ isLoading: false, error: message });
                    return false;
                }
            },

            cancelTwoFactor: () => {
                set({ twoFactorChallenge: null, error: null });
            },

            clearTwoFactorSetupRequired: () => {
                set({ twoFactorSetupRequired: false });
            },

            register: async (input: RegisterInput, client: ApolloClient<NormalizedCacheObject>) => {
                try {
                    set({ isLoading: true, error: null });

                    const { data } = await client.mutate({
                        mutation: REGISTER,
                        variables: { input },
                    });

                    if (data?.register) {
                        const { token, user } = data.register;

                        // Set token in Apollo Client
                        localStorage.setItem("token", token);

                        set({
                            user,
                            token,
                            isAuthenticated: true,
                            isLoading: false,
                            error: null,
                            currentOrganization: user.organizations?.[0]?.id || null,
                            orgRole: getOrgRole(user, user.organizations?.[0]?.id || null),
                            userRole: user.role,
                            shopifySession: null,
                        });

                        return true;
                    }

                    set({ isLoading: false, error: "Registration failed" });
                    return false;
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Registration failed";
                    set({ isLoading: false, error: errorMessage });
                    return false;
                }
            },

            logout: () => {
                localStorage.removeItem("token");
                set({ ...CLEARED_SESSION, sessionExpired: false });
                discardSessionData();
            },

            // `shopifySession` deliberately survives: a merchant installed from
            // Shopify has no floGPT password, so the login page has to send them
            // back to their admin rather than show a form they cannot complete.
            // A deliberate `logout` still clears it.
            expireSession: () => {
                localStorage.removeItem("token");
                set({ ...CLEARED_SESSION, shopifySession: get().shopifySession, sessionExpired: true });
                discardSessionData();
            },

            clearSessionExpired: () => {
                if (get().sessionExpired) set({ sessionExpired: false });
            },

            // User management
            fetchMe: async (client: ApolloClient<NormalizedCacheObject>, forceRefresh = false) => {
                try {
                    set({ isLoading: true, error: null });

                    const { data } = await client.query({
                        query: GET_ME,
                        fetchPolicy: forceRefresh ? "network-only" : "cache-first",
                    });

                    if (data?.me) {
                        const user = data.me;
                        // Keep the active selection when it's still a real org.
                        // Blindly resetting to organizations[0] used to drop a
                        // Shopify merchant with more than one org back into a
                        // non-Shopify workspace on every poll, which flipped the
                        // billing gate to the Stripe rail.
                        const selected = get().currentOrganization;
                        const stillValid =
                            !!selected && !!user.organizations?.some((o: any) => orgKey(o) === selected);
                        const nextOrg = stillValid ? selected : orgKey(user.organizations?.[0]);
                        set({
                            user,
                            isAuthenticated: true,
                            isLoading: false,
                            error: null,
                            currentOrganization: nextOrg,
                            orgRole: getOrgRole(user, nextOrg),
                            userRole: user.role,
                        });
                    } else {
                        set({ isLoading: false, error: "Failed to fetch user data" });
                    }
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Failed to fetch user data";
                    set({ isLoading: false, error: errorMessage });
                }
            },

            updateUser: async (input: UpdateUserInput, client: ApolloClient<NormalizedCacheObject>) => {
                try {
                    set({ isLoading: true, error: null });

                    const { data } = await client.mutate({
                        mutation: UPDATE_USER,
                        variables: { input },
                    });

                    if (data?.updateUser) {
                        const currentUser = get().user;
                        if (currentUser) {
                            set({
                                user: { ...currentUser, ...data.updateUser },
                                isLoading: false,
                                error: null,
                            });
                        }
                        return true;
                    }

                    set({ isLoading: false, error: "User update failed" });
                    return false;
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "User update failed";
                    set({ isLoading: false, error: errorMessage });
                    return false;
                }
            },

            // Organization management
            setCurrentOrganization: (organizationId: string) => {
                const user = get().user;
                set({
                    currentOrganization: organizationId,
                    orgRole: getOrgRole(user, organizationId),
                });
            },

            clearError: () => {
                set({ error: null });
            },
            // Initialize auth state from localStorage
            setAuth: (data) => {
                const { user, token, refreshToken: _refreshToken, expiresIn: _expiresIn, organizationId, shopDomain } = data;
                // Honour the caller's org (e.g. Shopify SSO lands in the store's
                // org) but only when the user actually belongs to it.
                const belongs =
                    !!organizationId && !!user.organizations?.some((o: any) => orgKey(o) === organizationId);
                const preferredOrg = belongs ? (organizationId as string) : orgKey(user.organizations?.[0]);
                set({
                    user,
                    token,
                    isAuthenticated: true,
                    isLoading: false,
                    error: null,
                    currentOrganization: preferredOrg,
                    orgRole: getOrgRole(user, preferredOrg),
                    userRole: user.role,
                    shopifySession: shopDomain ? { shopDomain } : null,
                });
                localStorage.setItem("token", token);
                rememberShopifyShop(shopDomain);
                // Note: refreshToken and expiresIn are not stored in the state, but are part of the payload.
            },

            initializeAuth: () => {
                const localToken = localStorage.getItem("token");
                if (isJwtExpired(get().token || localToken)) {
                    get().expireSession();
                    return;
                }
                if (localToken && !get().token) {
                    set({ token: localToken, isAuthenticated: true });
                }
            },
        }),
        {
            name: "auth-storage",
            partialize: (state) => ({
                token: state.token,
                user: state.user,
                isAuthenticated: state.isAuthenticated,
                currentOrganization: state.currentOrganization,
                orgRole: state.orgRole,
                userRole: state.userRole,
                shopifySession: state.shopifySession,
            }),
        },
    ),
);

// A token can expire while the tab is closed, and the restored state says
// nothing about it. Settling that before `_hasHydrated` opens the routes means
// the dashboard is never mounted against a session the server will refuse.
const dropExpiredSession = () => {
    const { token, expireSession } = useAuthStore.getState();
    if (token && isJwtExpired(token)) expireSession();
};

// Mark hydration complete after persist middleware finishes restoring state.
// Uses the persist API directly (more reliable than onRehydrateStorage callback).
if (useAuthStore.persist?.hasHydrated?.()) {
    dropExpiredSession();
    useAuthStore.setState({ _hasHydrated: true });
}
useAuthStore.persist?.onFinishHydration?.(() => {
    dropExpiredSession();
    useAuthStore.setState({ _hasHydrated: true });
});

/**
 * `can("manageBilling")` for the current organization.
 *
 * Chrome only — hiding an action the caller cannot complete. The resolver that
 * owns each capability checks it again, so a stale client cannot grant anything.
 */
export const useCan = (): OrgPermissionChecker => {
    const user = useAuthStore((s) => s.user);
    const currentOrganization = useAuthStore((s) => s.currentOrganization);
    const granted = getOrgPermissions(user, currentOrganization);
    // Joined rather than passed by reference: the array is rebuilt on every
    // `fetchMe`, and an unstable dependency here would rebuild every consumer's
    // memo on each poll.
    const key = granted ? granted.join(",") : null;
    return useMemo(() => buildOrgPermissionChecker(key === null ? null : key.split(",").filter(Boolean)), [key]);
};

export default useAuthStore;
export { useAuthStore };
